/* eslint-disable no-mixed-operators,object-shorthand,max-len */
import { get, findIndex, snakeCase } from 'lodash';
import { mapActions, mapState } from 'vuex';
import { formMixin, validators } from '@/components/forms/mixin';
import popupPageModeMixin from '@/mixins/popupPageModeMixin';
import userAgentMixin from '@/mixins/userAgent';
import { MODALS } from '@/components/app/app-modals/app-modals';

const CAPTCHA_FIELD = 'g-recaptcha-response';
const CACHE_NAME = 'register';
const MAX_CHARACTERS_USERNAME_PART = 5;

export default {
  mixins: [
    formMixin,
    popupPageModeMixin,
    userAgentMixin,
  ],
  data() {
    return {
      // login attempt counter used to rerender captcha on failed login
      attempt: 1,
      usernameShowed: false,
      usernameSource: '',
      fieldTypePassword: 'password',
      latestEmail: '',
      fields: {
        email: '',
        emailConfirmation: '',
        password: '',
        username: '',
        captcha: '',
      },
      timeout: null,
    };
  },
  validations: {
    fields: {
      email: validators.email,
      emailConfirmation: validators.emailConfirmation('email'),
      password: validators.password,
      username: validators.username,
      captcha: validators.captcha,
    },
  },
  computed: {
    ...mapState('popup', ['formDataCache']),
    showUsername() {
      if (this.usernameShowed) return true;
      const show = !this.$v.fields.email.$invalid && !this.$v.fields.emailConfirmation.$invalid;
      if (show) {
        if (this.fields.username === '') {
          const email = this.fields.email;
          this.usernameSource = snakeCase(email.substr(0, email.indexOf('@')));
          this.fields.username = this.suggestUserName();
          this.$v.fields.username.$touch();
          this.tabTo('_', 'password');
          this.usernameShowed = true;
        }
      } else {
        this.fields.username = '';
      }
      return show;
    },
    registerButtonDisabled() {
      return this.invalidOrDisabled || !this.fields.captcha;
    }
  },
  watch: {
    'fields.email'(value) {
      if (this.latestEmail && value && value !== this.latestEmail && this.formIsValid() && this.errors.length > 0) {
        this.formReset();
      }
    },
    'fields.emailConfirmation'(value) {
      if (this.latestEmail && value && value !== this.latestEmail && this.formIsValid() && this.errors.length > 0) {
        this.formReset();
      }
    },
  },
  methods: {
    ...mapActions('user', ['login', 'register']),
    ...mapActions('popup', ['setCurrentPopup', 'setCurrentSmallPopup', 'cacheFormData', 'setCurrentPopupOptions']),
    ...mapActions('profile', ['setProfileStatus']),
    showPassword() {
      this.fieldTypePassword = (this.fieldTypePassword === 'password') ? 'text' : 'password';
    },
    onLoginFail() {
      this.setCurrentPopup(MODALS.LOGIN);
    },
    loginAfterRegistration() {
      const payload = {
        [CAPTCHA_FIELD]: this.fields.captcha,
        email: this.fields.email,
        password: this.fields.password,
        grant_type: 'password',
      };
      if (this.isPage) {
        return this.login(payload)
          .then(() => this.$router.push({ name: 'home' }))
          .catch(this.onLoginFail);
      }

      return this.login(payload)
        .then(() => this.setCurrentPopup())
        .catch(this.onLoginFail);
    },
    onSubmit() {
      const payload = {
        onErrorRecaptchaToken: this.fields.captcha,
        email: this.fields.email,
        password: this.fields.password,
        username: this.fields.username,
      };

      this.$gtm.trackEvent({
        event: 'eventTrackerTrigger',
        category: 'Registration',
        action: 'submit',
        label: 'Create Account',
      });
      if (this.formIsValid() && this.formDisable()) {
        this.register(payload)
          .then(this.formEnable)
          .then(() => this.setProfileStatus({ event: 'registered' }))
          .then(this.loginAfterRegistration)
          .catch((e) => {
            const data = get(e, 'response.data', []);
            const errorCodesEmailExist = [
              'emailAlreadyInUse',
            ];
            const emailExistsIndex = findIndex(
              data, i => i.code && errorCodesEmailExist.includes(i.code));

            if (emailExistsIndex !== -1) {
              this.latestEmail = this.fields.email;
              this.setCurrentPopup(MODALS.ACCOUNT_ALREADY_EXISTS);
              this.setCurrentPopupOptions({ email: this.fields.email });
            } else {
              this.latestEmail = '';
            }
            this.attempt += 1;
            this.fields.captcha = null;
            const errorCodesToSuggestUsername = [
              'restrictedTextInUsername',
              'usernameAlreadyInUse',
            ];
            const isUsernameTakenIndex = findIndex(
              data, i => i.code && errorCodesToSuggestUsername.includes(i.code));

            if (isUsernameTakenIndex !== -1) {
              this.fields.username = this.suggestUserName();
            }

            this.formHandleErrors(e);
          });
      }
    },
    showUsernameInfo() {
      this.setCurrentSmallPopup(MODALS.SMALL_USERNAME);
    },
    suggestUserName() {
      return `${this.usernameSource.slice(0, MAX_CHARACTERS_USERNAME_PART)}${this.randomNumber()}`;
    },
    randomNumber(min = 10000, max = 99999) {
      return Math.floor(Math.random() * (max - min + 1) + min);
    },
    onTermsAndConditions() {
      const termsAndConditionsRoute = 'terms-and-conditions';
      if (this.$router.currentRoute.name === termsAndConditionsRoute) {
        this.setCurrentPopup();
      } else {
        this.$router.push({ name: termsAndConditionsRoute });
      }
    },
    hideFooter() {
      window.clearTimeout(this.timeout);
      this.timeout = null;
      this.setCurrentPopupOptions({ hideFooter: true });
    },
    showFooter() {
      this.timeout = window.setTimeout(() => {
        this.setCurrentPopupOptions({});
      }, 200);
    },
  },
  created() {
    if (this.formDataCache !== null && CACHE_NAME === this.formDataCache.route) {
      this.fields = {
        ...this.fields,
        ...this.formDataCache,
      };
    }
  },
  beforeDestroy() {
    this.cacheFormData({ ...this.fields, route: CACHE_NAME });
  },
};



// WEBPACK FOOTER //
// ./src/components/account/account-register/account-register.js